import assets from 'modules/assets'
import * as parse from 'modules/parse'

export function intro() {
  // Feeds into RowThreeText
  return {
    content: [
      [
        {
          type: 'h3',
          content: ['Our intent']
        }
      ],
      [
        {
          type: 'p',
          content: ['It’s easy to criticize education by claiming that schools are getting worse. But is this really the case?']
        },
        {
          type: 'p',
          content: ['In most cases, schools seem to be working just as fine as ever. We believe the problem is rooted not in the worsening of existing educational institutions, but in the dramatic shift of challenges and expectations we face in today’s society—challenges for which our education is supposed to prepare us. No matter how well-educated we are by the decades-old standards of existing systems, we cannot stay relevant to the present unless our education addresses the now.']
        }
      ],
      [
        {
          type: 'p',
          content: ['Gakko is our attempt to reconnect our education to the present. A present that, in psychologist Carl Rogers’ words, requires a shift from rigidity toward flexibility, from static living toward process living, from dependence toward autonomy, from defensiveness toward self-acceptance, from predictability toward creativity.']
        },
        {
          type: 'p',
          content: ['We aspire to create a new ecosystem of spaces, communities, and tools for learning that favors active engagement over passive exposure, genuine understanding over rote memorization, and personal integrity over external success.']
        }
      ]
    ]
  }
}

export function largeQuote() {
  // Feeds into RowThreeText
  return {
    large: true,
    content: [
      [
        {
          type: 'h3',
          content: ['']
        }
      ],
      [
        {
          type: 'h2',
          content: [parse.addSpaces('\"A shift from rigidity toward flexibility, // from static living toward process living, // from dependence toward autonomy, // from defensiveness toward self-acceptance, // from predictability toward creativity.\"')]
        },
        {
          type: 'h5',
          content: 'A Way of Being — Carl Rogers'
        }
      ]
    ]
  }
}

export function timeline(data) {
  // Feeds into RowThreeText
  return {
    title: 'History',
    content: [
      [
        {
          date: data['timeline-a'],
          title: data['timeline-a-title']
        },
        {
          date: data['timeline-b'],
          title: data['timeline-b-title']
        }
      ],
      [
        {
          date: data['timeline-c'],
          title: data['timeline-c-title']
        },
        {
          date: data['timeline-d'],
          title: data['timeline-d-title']
        }
      ],
      [
        {
          date: data['timeline-e'],
          title: data['timeline-e-title'],
          subTitle: data['timeline-e-subtitle']
        },
        {
          date: data['timeline-f'],
          title: data['timeline-f-title'],
          subTitle: data['timeline-f-subtitle']
        }
      ]
    ]
  }
}

export function vision(data) {
  // Feeds into RowThreeText
  return {
    content: [
      [
        {
          type: 'h3',
          content: ['Our Vision']
        }
      ],
      [
        {
          type: 'p',
          content: 'Our five years of experience building summer camps have led us to a new set of challenges: how can we offer the “magic” we create at our camps to more people, every day? How can we channel our creative energy to design a more holistic ecosystem in education, instead of just a summer camp?'
        },
        {
          type: 'p',
          content: 'In 2018, we are opening Gakko New York, a permanent space that offers Gakko’s unique education program throughout the year. Gakko New York will become the home for innovation, experimentation, and collaboration to build a more thoughtful education for the world.'
        }
      ],
      [
        {
          type: 'p',
          content: 'At Gakko New York, we will offer learner-centered after-school programs for middle school and high school students that focus on project-based learning, personal mentorship for students to connect their everyday learning to their long term life goals, and seminars for educators by thought leaders from various disciplines. If you want to find out more, work with us, and/or get in touch, please send us an email.'
        },
        {
          type: 'h5',
          content: 'Email us to get involved',
          mailTo: true,
          linkURL: 'mailto:hello@gakkoproject.com?Subject=Hello'
        }
      ]
    ]
  }
}

export function ourStudios(data) {
  // Feeds into RowThreeText
  return {
    large: true,
    content: [
      [
        {
          type: 'h3',
          content: ['Our Studios']
        }
      ],
      [
        {
          type: 'h3',
          style: 'sectra-light',
          content: ['New York']
        },
        {
          type: 'p',
          hasMarginTop: true,
          content: `243 Canal Street, 5F\n
            New York, NY, 10013 (currently under renovation)\n\n
            Temporary location: 67 West Street, Suite 401, Brooklyn, NY, 11222\n`
        },
        {
          type: 'map',
          center: {
            lat: 40.7183062,
            lng: -73.9999135
          },
          zoom: 15,
          link: 'https://goo.gl/maps/mVLUo37yCJp'
        },
        {
          type: 'h3',
          style: 'sectra-light',
          content: ['San Francisco']
        },
        {
          type: 'p',
          hasMarginTop: true,
          content: `730 Florida Street, #9\n
            San Francisco, CA, 94110\n`
        },
        {
          type: 'map',
          center: {
            lat: 37.760135,
            lng: -122.411286
          },
          zoom: 15,
          link: 'https://goo.gl/maps/98XFzVF4CQL2'
        },
        {
          type: 'h3',
          style: 'sectra-light',
          content: ['Tokyo']
        },
        {
          type: 'p',
          hasMarginTop: true,
          content: `(株) GAKKO\n
                    東京都港区麻布台3-1-5, 8F\n
                    3-1-5-8F Azabudai, Minato-ku, Tokyo\n
                    Tel: +81 3-5797-7140\n
                    Country Manager: Reiri Ray Kojima`
        },
        {
          type: 'map',
          center: {
            lat: 35.661133,
            lng: 139.738318
          },
          zoom: 15,
          link: 'https://goo.gl/maps/pKmwPGZjPn12'
        }
      ]
    ]
  }
}

export const teams = {
  SF: 0,
  NY: 1,
  JAP: 2
}

export function team(data, whichTeam) {
  let teamData = data.location.map((loc, i) => {
    return {
      title: {
        type: 'h3',
        style: 'sectra-light',
        content: [loc.name]
      },
      content: loc.members.map((member, i) => {
        return {
          imageURL: assets('images/team/' + member.id + '.jpg'),
          title: member.name,
          subTitle: member.position,
          description: member.message
        }
      })
    }
  })

  if (whichTeam === teams.SF) {
    return Object.assign({}, {sectionTitle: 'The Team'}, teamData[whichTeam])
  }

  return Object.assign({}, teamData[whichTeam])
}

export function advisors(teamData) {
  return {
    title: {
      type: 'h3',
      style: 'sectra-light',
      content: ['Advisors']
    },
    content: [
      {
        imageURL: assets('images/advisors/' + 'pratury' + '.jpg'),
        title: 'Lakshmi Pratury',
        subTitle: 'CEO of INK',
        description: 'Lakshmi is an entrepreneur, curator, interviewer, and speaker. After more than 20 years in leading roles in tech, venture capital, and non-profits, she founded INK, India’s foremost platform for the exchange of cutting-edge ideas.'
      },
      {
        imageURL: assets('images/advisors/' + 'hardy' + '.jpg'),
        title: 'John Hardy',
        subTitle: 'Founder of Green School, Bali',
        description: ' An artist and entrepreneur, John dedicates himself to building a more sustainable world through education and design. He and his wife Cynthia conceived and created the Green School in Bali, Indonesia to deliver pioneering education.'
      },
      {
        imageURL: assets('images/advisors/' + 'maria' + '.jpg'),
        title: 'María de Lera',
        subTitle: 'Lyft',
        description: 'María wants to use technology as a force for social change. An early GAKKO ambassador, she brings her experiences in operations strategy at Lyft and technology investment banking at Credit Suisse to her role as the team\'s Strategic Advisor.'
      },
      {
        imageURL: assets('images/advisors/' + 'gersick' + '.jpg'),
        title: 'Lauren Gersick',
        subTitle: 'The Urban School of San Francisco',
        description: 'Lauren has worked in college counseling for over a decade, with a deep interest in the creative paths of students. Since 2013, she has been at The Urban School of San Francisco, where she is currently Co-director of College Counseling.'
      },
      {
        imageURL: assets('images/advisors/' + 'gross' + '.jpg'),
        title: 'Bay Gross',
        subTitle: 'Sidewalk Labs',
        description: 'Bay is a product manager and technology enthusiast who has worked with Google in New York, India, and Switzerland. He currently works for a Venture Capital firm in Manhattan and enjoys working closely with great startups.'
      },
      {
        imageURL: assets('images/advisors/' + 'tom' + '.jpg'),
        title: 'Tom Lamb',
        subTitle: 'Senior Strategy Advisor',
        description: 'Tom is a technology company founder and executive, ex-Wall Street lawyer and investment banker, who has worked extensively both within and outside of the United States. He now focuses his professional efforts on advising startup companies from his home in Southern California.'
      }
      // {
      //   imageURL: assets('images/advisors/' + 'eikoh' + '.jpg'),
      //   title: 'Eikoh Harada',
      //   subTitle: 'Senior Strategy Advisor, Japan',
      //   description: 'As former VP of Apple (USA), CEO of Benesse, and Director of Sony, Eikoh brings his 33 years of experience as a top executive in IT industry and education to help Gakko with its Digital Learning initiative and expansion to Asia Pacific.'
      // }
    ]
  }
}

export function hinoki() {
  // Feeds into RowThreeText
  return {
    content: [
      [
        {
          type: 'h3',
          content: ['Our sponsor']
        }
      ],
      [
        {
          type: 'p',
          content: 'Hinoki was founded in 1955 in Nihonbashi, Tokyo, upon the purchase of its first real estate property. Since its establishment, Hinoki’s primary business has been the leasing of its company-owned properties. Hinoki has earned its tenants’ trusts by offering real estate that gives utmost care towards ‘security, safety, quality and the environment.’ Recently, Hinoki has also begun to make social investments in many of its tenants that are operating startup ventures, as well as providing support to various music projects.'
        },
        {
          type: 'p',
          content: 'In the summer of 2013, Hinoki’s Itsuki Garden, a Kamakura café terrace garden, hosted the Gakko Project’s 2013 completion celebration, and a new friendship was born. With Rafi, Kenta’s co-director, and all of the excellent teachers and students, we believe that the Gakko Project will make a positive impact to the nature of education itself, in Japan.'
        }
      ],
      [
        {
          type: 'p',
          content: 'We also firmly endorse the Gakko Project’s commitment to enable equal access to all students, regardless of socio-economic background, race or nationality.'
        },
        {
          type: 'p',
          content: 'As Hinoki enters if 61st year of history, we carry forward our respect for Kenta, a young leader that aspired to start the Gakko Project while studying overseas, and our desire to share his way of thinking.'
        }
      ]
    ]
  }
}



// WEBPACK FOOTER //
// ./src/modules/ourStory.js